import { Map } from 'mapbox-gl'
import { copyBlobToClipboard } from './clipboard'
import { MapLocation } from '@app/redux/riskMap/riskMapSlice'
import { Theme } from '@mui/material'
import {
  PinSvg,
  stripWidthAndHeightFromSvg,
} from '@app/components/Molecules/MapView/RiskMapView/LayerTypes'
import { DATADOG_ACTIONS, PUBLIC_ASSETS_BASE_URL } from '@app/app-constants'
import {
  createHiddenDivAndAddToPage,
  getMapImageAsBlob,
  deleteDiv,
} from '../../../../Molecules/Reports/mapExporterUtils'
import { logDataDogAction } from '@app/utils/logger'
import { getSvgDimensions } from '@app/modules/Live/Live.utils'

export const exportMapToClipboard = async (theMap: Map, vulnerabilityPalette: string[]) => {
  const hiddenDiv = createHiddenDivAndAddToPage(theMap.getCanvas().width, theMap.getCanvas().height)

  const mapCopy = await duplicateMap(theMap, hiddenDiv, vulnerabilityPalette)

  const mapImageBlob = await getMapImageAsBlob(mapCopy)
  copyBlobToClipboard(mapImageBlob)

  mapCopy.remove()
  deleteDiv(hiddenDiv)
}

export const preparePageDuplicateMapAndExportImageBlob = async (
  theMap: Map,
  widthInPoints: number,
  heightInPoints: number,
  imageBlobOutputHandler: (imageBlob: Blob) => Promise<void>,
  vulnerabilityPalette: string[],
  selectedElementMarkerLocation: MapLocation | null = null,
  theme: Theme | null = null,
) => {
  const dpi = 300 // High quality printing
  const pointSize = 1 / 72

  const aspectRatio = widthInPoints / heightInPoints

  const mapWidthInPixels = theMap.getCanvas().width
  const mapHeightInPixels = mapWidthInPixels / aspectRatio

  const mapWidthInDots = widthInPoints * pointSize * dpi

  const imageScale = mapWidthInDots / mapWidthInPixels

  const currentWindowScale = getWindowScale()
  scaleWindow(currentWindowScale * imageScale)

  const hiddenDiv = createHiddenDivAndAddToPage(mapWidthInPixels, mapHeightInPixels)

  const mapCopy = await duplicateMap(theMap, hiddenDiv, vulnerabilityPalette)

  if (selectedElementMarkerLocation && theme) {
    await addSelectedElementMarkerToMap(mapCopy, selectedElementMarkerLocation, theme)
  }

  const mapImageBlob = await getMapImageAsBlob(mapCopy)

  await imageBlobOutputHandler(mapImageBlob)

  mapCopy.remove()
  deleteDiv(hiddenDiv)

  scaleWindow(currentWindowScale)
}

// The selected element marker on the OG map is an html element that sits on top of the map canvas
// so to get this to render on the map canvas we need to add an icon layer
const addSelectedElementMarkerToMap = async (
  map: Map,
  selectedElementMarkerLocation: MapLocation,
  theme: Theme,
) => {
  const selectedElementIconResponse = await fetch(
    `${PUBLIC_ASSETS_BASE_URL}/icons/selected_element.svg`,
  )
  const selectedElementIconSvg = stripWidthAndHeightFromSvg(
    await selectedElementIconResponse.text(),
  )

  const pinSvg = PinSvg.replace('<!--ICONSVG-->', selectedElementIconSvg).replace(
    '#777777',
    theme.palette.primary.main,
  )

  const svgBitmap = new Image(42, 51)
  svgBitmap.onload = () => {
    map.addImage('selecterElementMarker', svgBitmap)
  }
  svgBitmap.src = 'data:image/svg+xml;base64,' + btoa(pinSvg)

  map.addSource('selectedElementLocationSource', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Point',
            coordinates: [
              selectedElementMarkerLocation.longitude,
              selectedElementMarkerLocation.latitude,
            ],
          },
        },
      ],
    },
  })

  map.addLayer({
    id: 'selectedElementLocation',
    type: 'symbol',
    source: 'selectedElementLocationSource',
    layout: {
      'icon-image': 'selecterElementMarker',
      'icon-size': 2, // Make it twice as big
      'icon-anchor': 'bottom',
    },
  })

  // Wait for the layer to load
  return new Promise<void>(function (resolve) {
    map.on('idle', () => {
      resolve()
    })
  })
}

export const blobToDataUrl = async (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onloadend = () => {
      if (reader.result) resolve(reader.result.toString())
      else reject(new Error('Failed to convert image to data url'))
    }
    reader.readAsDataURL(blob)
  })

/**
 * Layer feature state is not automatically copied when sources are cloned.
 * This function will copy all feature state from `fromMap` to `toMap`.
 */
const cloneFeatureState = (fromMap: Map, toMap: Map): void => {
  const style = fromMap.getStyle()
  if (!style || !style.layers) return
  for (const layer of style.layers) {
    if (!('source' in layer)) continue
    if (!('source-layer' in layer)) continue

    const source = layer.source as string
    const sourceLayer = layer['source-layer']

    if (!source || !sourceLayer) continue

    const features = fromMap.querySourceFeatures(source, { sourceLayer })
    for (const feature of features) {
      if (feature.id == null) continue

      const state = fromMap.getFeatureState({ source: source, sourceLayer, id: feature.id })

      if (!state || Object.keys(state).length === 0) continue

      toMap.setFeatureState({ source, sourceLayer, id: feature.id }, state)
    }
  }
}

const duplicateMap = async (
  theMap: Map,
  hiddenDiv: HTMLDivElement,
  vulnerabilityPalette: string[],
): Promise<Map> =>
  new Promise((resolve) => {
    const mapCopy = new Map({
      container: hiddenDiv,
      style: theMap.getStyle(),
      center: theMap.getCenter(),
      bearing: theMap.getBearing(),
      pitch: theMap.getPitch(),
      zoom: theMap.getZoom(),
    })

    // Icon layer images aren't replicated above and need to be loaded separately.
    // I went round-the-houses trying to reload the images in all sorts of ways but
    // had major issues with race conditions and daft logic so settled on loading
    // an image when it throws a not found error as it plays nice with the on idle event.
    mapCopy.on('styleimagemissing', async (e) => {
      const vulnerabilityIconId = e.id as string

      const isLiveMap = window.location.href.includes('app/live')

      // vulnerabilityIconId should look like 'water_house-3' for example where the number after
      // the hyphen is the vulnerability value
      const hyphenPos = vulnerabilityIconId.lastIndexOf('-')
      if (hyphenPos == -1) return

      const iconId = isLiveMap
        ? vulnerabilityIconId.split('-')[0]
        : vulnerabilityIconId.substring(0, hyphenPos)
      const vulnerability = vulnerabilityIconId.substring(hyphenPos + 1)
      if (vulnerability !== 'default' && Number.isNaN(Number(vulnerability)) && !isLiveMap) return

      let pinColor = ''
      if (vulnerability == 'default') pinColor = '#0b2a48'
      else pinColor = vulnerabilityPalette[Number(vulnerability)]

      if (Number.isNaN(vulnerability)) return

      let response = null
      let pinSvg = PinSvg
      let iconSvg = null
      let svgBitmap = null
      let svgHeight = 42
      let svgWidth = 51
      let renderSvg = null

      if (isLiveMap) {
        const liveIconId = vulnerabilityIconId.split(`${iconId}-`)[1]

        response = await fetch(
          `${PUBLIC_ASSETS_BASE_URL}/icons/live_feeds/${iconId}/${liveIconId}.svg`,
        )
        iconSvg = await response.text()
        const svgDimensions = getSvgDimensions(iconSvg)
        if (svgDimensions) {
          svgHeight = svgDimensions.height
          svgWidth = svgDimensions.width
        }
        renderSvg = iconSvg
      } else {
        response = await fetch(`${PUBLIC_ASSETS_BASE_URL}/icons/${iconId}.svg`)
        iconSvg = stripWidthAndHeightFromSvg(await response.text())
        pinSvg = pinSvg.replace('<!--ICONSVG-->', iconSvg)
        pinSvg = pinSvg.replace('#777777', pinColor)
        renderSvg = pinSvg
      }

      svgBitmap = new Image(svgHeight, svgWidth)
      svgBitmap.onload = () => {
        if (!mapCopy.hasImage(e.id)) mapCopy.addImage(e.id, svgBitmap)
      }
      svgBitmap.src = 'data:image/svg+xml;base64,' + btoa(renderSvg)
    })

    mapCopy.on('load', () => {
      cloneFeatureState(theMap, mapCopy)

      // Count billed map loads
      logDataDogAction(DATADOG_ACTIONS.MAPBOX_MAP_LOADED, {
        context: 'print',
      })
    })

    mapCopy.once('idle', () => {
      resolve(mapCopy)
    })
  })

function getWindowScale() {
  return window.devicePixelRatio
}

function scaleWindow(scale: number) {
  Object.defineProperty(window, 'devicePixelRatio', {
    get: function () {
      return scale
    },
  })
}
